import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "./ui/use-toast";

const ContactForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    whatsapp: "",
    package: "",
    message: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.whatsapp || !formData.package) {
      toast({
        title: "Mohon lengkapi data",
        description: "Nama, WhatsApp, dan Paket wajib diisi.",
        variant: "destructive",
      });
      return;
    }

    const message = encodeURIComponent(
      `Halo Admin Toyibah Aqiqah 👋,\n\nSaya ingin memesan layanan aqiqah:\nNama: ${formData.name}\nNo. WhatsApp: ${formData.whatsapp}\nPaket: ${formData.package}\nPesan: ${formData.message || "-"}\n\nMohon bantu proses selanjutnya. Terima kasih 🙏`
    );

    window.open(`https://wa.me/6287884243038?text=${message}`, "_blank");

    toast({
      title: "Mengarahkan ke WhatsApp...",
      description: "Silakan lanjutkan percakapan dengan admin kami.",
      duration: 3000,
    });

    setFormData({
      name: "",
      whatsapp: "",
      package: "",
      message: "",
    });
  };

  return (
    <section
      id="contact" // ✅ penting! agar tombol "Kontak" di header bisa scroll ke sini
      className="relative py-16 md:py-20 px-6 bg-gradient-to-b from-[#FFF9F7] via-[#FFF5F2] to-[#FFFFFF] overflow-hidden pt-28 scroll-mt-20"
    >
      {/* ✨ Background Pattern */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='24' height='24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 1L12 23M1 12L23 12' stroke='%23FF8A65' stroke-width='0.4'/%3E%3C/svg%3E")`,
          backgroundRepeat: "repeat",
        }}
      />

      <div className="relative z-10 max-w-3xl mx-auto text-center">
        {/* Heading */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-2xl md:text-4xl font-extrabold text-[#333] mb-4"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          Siap Mengaqiqahkan{" "}
          <span className="text-[#FF8A65]">Buah Hati Anda?</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="text-[#777] text-sm md:text-base mb-10 max-w-2xl mx-auto leading-relaxed"
        >
          Isi data di bawah ini untuk langsung terhubung dengan admin kami via
          WhatsApp. Proses mudah, cepat, dan amanah.
        </motion.p>

        {/* 💬 Form */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white/90 backdrop-blur-sm border border-[#FFD8C8]/50 rounded-2xl shadow-[0_6px_20px_rgba(0,0,0,0.05)] p-6 md:p-8 text-left max-w-md mx-auto"
        >
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Input
                id="name"
                placeholder="Nama Lengkap"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="h-11 text-sm md:text-base bg-gray-50 focus:bg-white border-[#FFE3D8] focus:border-[#FF8A65] transition-all"
              />
            </div>

            <div>
              <Input
                id="whatsapp"
                type="tel"
                placeholder="Nomor WhatsApp"
                value={formData.whatsapp}
                onChange={(e) =>
                  setFormData({ ...formData, whatsapp: e.target.value })
                }
                className="h-11 text-sm md:text-base bg-gray-50 focus:bg-white border-[#FFE3D8] focus:border-[#FF8A65] transition-all"
              />
            </div>

            <div>
              <Select
                value={formData.package}
                onValueChange={(value) =>
                  setFormData({ ...formData, package: value })
                }
              >
                <SelectTrigger className="h-11 text-sm md:text-base bg-gray-50 focus:bg-white border-[#FFE3D8] focus:border-[#FF8A65] transition-all">
                  <SelectValue placeholder="Pilih Paket Aqiqah" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Paket A Ekonomis">
                    Paket A Ekonomis (Rp 2.2jt)
                  </SelectItem>
                  <SelectItem value="Paket B Standar">
                    Paket B Standar (Rp 3.5jt)
                  </SelectItem>
                  <SelectItem value="Paket C Favorit">
                    Paket C Favorit (Rp 4.9jt)
                  </SelectItem>
                  <SelectItem value="Paket D Premium">
                    Paket D Premium (Rp 6.5jt)
                  </SelectItem>
                  <SelectItem value="Konsultasi Dulu">
                    Konsultasi Dulu
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Textarea
                id="message"
                placeholder="Pesan tambahan (opsional)..."
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
                className="text-sm md:text-base bg-gray-50 focus:bg-white border-[#FFE3D8] focus:border-[#FF8A65] transition-all min-h-[90px]"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white py-5 rounded-xl font-bold text-base md:text-lg shadow-lg transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2"
            >
              <Send className="h-4 w-4 md:h-5 md:w-5" />
              Chat via WhatsApp
            </Button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactForm;
